import type { App } from 'vue';
import newRelicLoader from '@/plugins/newrelic-loader.js';

type NewRelicAgent = NonNullable<ReturnType<typeof newRelicLoader.getAgent>>;

const RESERVED_ATTRIBUTES = [
    'accountId',
    'appId',
    'entityGuid',
    'timestamp',
    'duration',
    'name',
    'type',
    'eventType',
] as const;

const prefixReservedAttributes = (attributes: unknown): unknown => {
    if (!attributes || typeof attributes !== 'object') {
        return attributes;
    }

    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(attributes as Record<string, unknown>)) {
        const newKey = (RESERVED_ATTRIBUTES as readonly string[]).includes(key)
            ? `custom.${key}`
            : key;
        result[newKey] = value;
    }
    return result;
};

const NewRelicBrowser = {
    get agent(): NewRelicAgent | null {
        return newRelicLoader?.getAgent?.() ?? null;
    },

    withAgent(callback: (agent: NewRelicAgent) => void) {
        const agent = this.agent;
        if (agent) {
            callback(agent);
        }
    },

    setAuthUser(user: { id?: string | number; account_id?: string | number } | null) {
        if (!user?.id) {
            return;
        }

        this.withAgent((agent) => {
            agent.setUserId(String(user.id));
            agent.setCustomAttribute('userId', String(user.id));
            agent.setCustomAttribute('userAccountId', String(user.account_id));
        });
    },

    clearAuthUser() {
        this.withAgent((agent) => {
            agent.setUserId(null);
            agent.setCustomAttribute('userId', null);
            agent.setCustomAttribute('userAccountId', null);
        });
    },

    setContextAccount(accountId: string | number | null) {
        this.withAgent((agent) => {
            agent.setCustomAttribute('contextAccountId', accountId ? String(accountId) : null);
        });
    },

    setPageViewName(name: string) {
        this.withAgent((agent) => {
            agent.setPageViewName(name);
        });
    },

    setCurrentRouteName(name: string) {
        this.withAgent((agent) => {
            agent.setCurrentRouteName(name);
        });
    },

    addPageAction(actionName: string, attributes?: Record<string, unknown>) {
        this.withAgent((agent) => {
            const safeAttributes = prefixReservedAttributes(attributes);
            agent.addPageAction(actionName, safeAttributes);
        });
    },
};

const VueNewRelic = {
    installed: false,

    install(app: App) {
        if (this.installed) {
            return;
        }

        this.installed = true;
        app.config.globalProperties.$newRelic = NewRelicBrowser;
    },
};

export { VueNewRelic };
export default NewRelicBrowser;
